import React, { Dispatch, useState } from 'react';
import { useTranslation } from 'react-i18next';
import DialogHeader from '../_parts/DialogHeader';
import { useDispatch, useSelector } from 'react-redux';
import { IAppReduxStore } from '../../../../redux/store';
import DialogFrame from '../_parts/DialogFrame';
import { useParams } from 'react-router-dom';
import {
    Button,
    DialogActions,
    DialogContent,
    FormLabel,
    MenuItem,
    Select,
    SelectChangeEvent,
    SxProps,
    TextField,
    Stack,
} from '@mui/material';
import { ContentLayout } from '../_parts/ContentLayout';
import { bodyFont, headingFontBold, vars } from '../../../../Theme';
import Message from '../_parts/Message';
import { mdiAlertCircle } from '@mdi/js';
import {
    databaseAccessReasons,
    postProjectAccessLog,
    toggleDatabaseAccessDialog,
} from '../../../../redux/general/sliceDatabaseAccess';

const formLabelSx: SxProps = {
    ...headingFontBold,
    fontSize: '16px',
    color: vars.black,
    fontWeight: 700,
    marginBottom: '8px',

    '&.Mui-focused': {
        color: vars.black,
    },
};

const textFieldSx: SxProps = {
    input: { ...bodyFont, p: 0 },

    '& .MuiOutlinedInput-root': {
        '&.Mui-focused fieldset': {
            border: `2px solid ${vars.mainGreen}`,
        },
    },
};

const buttonSx: SxProps = {
    textTransform: 'none',
    fontWeight: 700,
    fontSize: '14px',
    fontFamily: headingFontBold,
    lineHeight: 'normal',
    padding: '9px 12px',
    color: vars.white,
};

const ErrorMessage = ({ text }: { text: string }) => {
    return (
        <Message
            text={text}
            icon={mdiAlertCircle}
            color={vars.errorDialogRed}
            typographySx={{ fontWeight: 700 }}
            wrapperSx={{
                border: 'none',
                padding: '2px',
                mb: 1,
            }}
        />
    );
};

const initialMainState: IDatabaseAccessMainState = {
    reason: '',
    comment: '',
};

const initialErrorState: IDatabaseAccessErrorState = {
    reason: false,
};

export default () => {
    const { t } = useTranslation('translations', { keyPrefix: 'databaseAccessDialog' });
    const dispatch: Dispatch<unknown> = useDispatch();

    const { replicaId } = useParams();

    const { isDialogOpen, loading } = useSelector(
        (_store: IAppReduxStore) => _store.databaseAccess,
    );

    const [mainState, setMainState] = useState<IDatabaseAccessMainState>(initialMainState);
    const [showErrorState, setShowErrorState] =
        useState<IDatabaseAccessErrorState>(initialErrorState);

    const handleChange = ({ field, value }: { field: string; value: string }) => {
        setShowErrorState({ ...showErrorState, [field]: false });
        setMainState(prevState => ({
            ...prevState,
            [field]: value,
        }));
    };

    const resetState = () => {
        setShowErrorState(initialErrorState);
        setMainState({ ...initialMainState });
    };

    const onClose = () => {
        resetState();
        dispatch(toggleDatabaseAccessDialog(false));
    };

    const onCancel = () => {
        onClose();
        window.location.href = '/';
    };

    const validateInput = () => {
        setShowErrorState({
            reason: mainState.reason === '',
        });
        return mainState.reason !== '';
    };
    const onLogDatabaseAccess = () => {
        if (validateInput()) {
            dispatch(postProjectAccessLog({ replicaId, ...mainState }));
            onClose();
        }
    };

    return (
        <DialogFrame open={isDialogOpen}>
            <DialogHeader onClose={onCancel} dialogTitle={t('dialogTitle')} />
            <DialogContent sx={{ pt: 0, overflow: 'hidden' }}>
                <ContentLayout
                    loading={null}
                    documentSelection={null}
                    main={
                        <Stack spacing={2}>
                            <Stack spacing={0}>
                                <FormLabel required sx={formLabelSx}>
                                    {t('reasonLabel')}
                                </FormLabel>
                                {showErrorState.reason && (
                                    <ErrorMessage text={t('errorMakeSelection')} />
                                )}
                                <Select
                                    size='small'
                                    value={mainState.reason}
                                    error={showErrorState.reason}
                                    onChange={(e: SelectChangeEvent<string>) => {
                                        handleChange({ field: 'reason', value: e.target.value });
                                    }}
                                    displayEmpty={!mainState.reason}
                                    inputProps={{ 'aria-label': 'Without label' }}
                                >
                                    {databaseAccessReasons?.map(option => (
                                        <MenuItem
                                            key={option}
                                            value={option}
                                            sx={{ whiteSpace: 'normal' }}
                                        >
                                            {option}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </Stack>

                            <Stack spacing={0}>
                                <FormLabel sx={formLabelSx}>{t('commentLabel')}</FormLabel>
                                <TextField
                                    size='small'
                                    sx={textFieldSx}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                        handleChange({ field: 'comment', value: e.target.value })
                                    }
                                    placeholder={t('commentPlaceholder')}
                                    value={mainState.comment}
                                    multiline
                                />
                            </Stack>
                        </Stack>
                    }
                />
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 1, borderTop: `1px solid ${vars.lightGray}` }}>
                <Button
                    variant={'text'}
                    onClick={onCancel}
                    sx={{ ...buttonSx, color: vars.font }}
                    disabled={loading}
                >
                    {t('ctaCancel')}
                </Button>
                <Button
                    variant={'contained'}
                    onClick={onLogDatabaseAccess}
                    sx={buttonSx}
                    disabled={loading}
                >
                    {t('ctaOk')}
                </Button>
            </DialogActions>
        </DialogFrame>
    );
};

export interface IDatabaseAccessMainState {
    reason: string;
    comment: string;
}

export interface IDatabaseAccessErrorState {
    reason: boolean;
}
